import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { hasUsableIdentity } from "@/lib/company-resolution";
import {
  type HandlePositions,
  type CentralNodeData,
  type PersonNodeData,
  type CompanyNodeData,
  type PersonEntry,
  type CompanyRow,
  PERSON_COLOR,
  RELATIONSHIP_COLORS,
  CENTRAL_SIZE,
  PERSON_SIZE,
  COMPANY_NODE_WIDTH,
  COMPANY_NODE_HEIGHT,
  buildPersonRegistry,
  buildSpiderGraph,
  applySpiderLayout,
  buildCompanyRows,
} from "@/lib/company-relationship-graph";
import {
  CompanyDTO,
} from "@shaarp/common";
import {
  Background,
  BackgroundVariant,
  Handle,
  type Node,
  type NodeMouseHandler,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDownIcon, Loader2Icon, MaximizeIcon, NetworkIcon } from "lucide-react";
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────

type CardCompanyRelationshipGraphProps = {
  readonly company: CompanyDTO;
};

// ─── Custom Node Components ───────────────────────────────────────────

function DynamicHandles({ handlePositions, type }: { readonly handlePositions?: HandlePositions; readonly type: "source" | "target" }) {
  if (!handlePositions) return null;
  return (
    <>
      {Object.entries(handlePositions).map(([id, pos]) => (
        <Handle
          key={id}
          id={id}
          type={type}
          position={Position.Top}
          className="!opacity-0"
          style={{ top: `${pos.top}%`, left: `${pos.left}%` }}
        />
      ))}
    </>
  );
}

const CentralCompanyNode = memo(function CentralCompanyNode({
  data,
}: NodeProps<Node<CentralNodeData>>) {
  return (
    <div
      className="flex items-center justify-center rounded-full bg-slate-800 text-white font-semibold shadow-lg"
      style={{ width: CENTRAL_SIZE, height: CENTRAL_SIZE }}
    >
      <DynamicHandles handlePositions={data.handlePositions} type="source" />
      <DynamicHandles handlePositions={data.handlePositions} type="target" />
      <span className="px-3 text-center text-xs leading-tight line-clamp-3">
        {data.label}
      </span>
    </div>
  );
});

const PersonNode = memo(function PersonNode({
  data,
}: NodeProps<Node<PersonNodeData>>) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-full bg-white shadow-md"
      style={{
        width: PERSON_SIZE,
        height: PERSON_SIZE,
        border: `3px solid ${PERSON_COLOR}`,
      }}
      title={data.role ?? ""}
    >
      <DynamicHandles handlePositions={data.handlePositions} type="target" />
      <DynamicHandles handlePositions={data.handlePositions} type="source" />
      <span className="px-2 text-center text-[10px] font-medium leading-tight line-clamp-3 text-slate-800">
        {data.label}
      </span>
    </div>
  );
});

const CompanyNodeComponent = memo(function CompanyNodeComponent({
  data,
}: NodeProps<Node<CompanyNodeData>>) {
  const color = RELATIONSHIP_COLORS[data.relationshipType];
  return (
    <div
      className="flex flex-col items-center justify-center rounded-xl bg-white shadow-sm"
      style={{
        width: COMPANY_NODE_WIDTH,
        height: COMPANY_NODE_HEIGHT,
        border: `2px solid ${color}`,
        cursor: data.siren ? "pointer" : "default",
      }}
      title={
        [data.siren ? `SIREN: ${data.siren}` : null, data.role]
          .filter(Boolean)
          .join(" — ") || undefined
      }
    >
      <DynamicHandles handlePositions={data.handlePositions} type="target" />
      <DynamicHandles handlePositions={data.handlePositions} type="source" />
      <span className="px-2 text-center text-[11px] font-medium leading-tight line-clamp-2 text-slate-800">
        {data.label}
      </span>
      {data.role && (
        <span className="px-2 text-center text-[9px] leading-tight text-slate-500 line-clamp-1">
          {data.role}
        </span>
      )}
    </div>
  );
});

const nodeTypes = {
  central: CentralCompanyNode,
  person: PersonNode,
  company: CompanyNodeComponent,
};

// ─── Opening a related company ────────────────────────────────────────

/**
 * The establishments of a SIREN looked at for one to open: the search matches
 * the SIREN exactly, and its first row may be a nameless placeholder.
 */
const RELATED_COMPANY_CANDIDATES = 5;

type OpenCompanyBySiren = {
  readonly openCompany: (siren: string) => Promise<void>;
  /** The SIREN being looked up: no other is opened meanwhile. */
  readonly openingSiren: string | null;
};

/**
 * Opens the page of the company a SIREN names when the base holds it under a
 * name. One the base does not hold — or holds only as a nameless placeholder,
 * which would open an empty page — has no page: the click says so, and the
 * chat can look it up. One lookup at a time: a second click waits for it.
 */
function useOpenCompanyBySiren(): OpenCompanyBySiren {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const toastError = useToastError();
  const [openingSiren, setOpeningSiren] = useState<string | null>(null);
  const isOpening = useRef(false);

  const openCompany = useCallback(
    async (siren: string) => {
      if (isOpening.current) return;
      isOpening.current = true;
      setOpeningSiren(siren);
      try {
        const { data: results } = await api.get<CompanyDTO[]>(
          "/companies/available",
          { params: { search: siren, limit: RELATED_COMPANY_CANDIDATES } },
        );
        const named = results.find(hasUsableIdentity);
        if (named) {
          navigate(`/company/${named.id}`);
          return;
        }
        toast({
          variant: "default",
          title: t("company.relationships.unknownCompany.title"),
          description: t("company.relationships.unknownCompany.description"),
        });
      } catch {
        toastError();
      } finally {
        isOpening.current = false;
        setOpeningSiren(null);
      }
    },
    [api, navigate, t, toast, toastError],
  );

  return { openCompany, openingSiren };
}

// ─── Spider Graph Component ───────────────────────────────────────────

function SpiderGraph({
  company,
  opening: { openCompany, openingSiren },
  height = "500px",
}: {
  readonly company: CompanyDTO;
  readonly opening: OpenCompanyBySiren;
  readonly height?: string;
}) {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const graph = buildSpiderGraph(company);
    return applySpiderLayout(graph);
  }, [company]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [baseEdges] = useEdgesState(initialEdges);

  const edges = useMemo(() => {
    if (!hoveredNodeId) return baseEdges;
    return baseEdges.map((edge) => {
      const isConnected = edge.source === hoveredNodeId || edge.target === hoveredNodeId;
      return {
        ...edge,
        style: {
          ...edge.style,
          opacity: isConnected ? 1 : 0.15,
          strokeWidth: isConnected
            ? ((edge.style?.strokeWidth as number) ?? 2) + 1
            : (edge.style?.strokeWidth as number) ?? 2,
        },
        labelStyle: {
          ...edge.labelStyle,
          opacity: isConnected ? 1 : 0.15,
        },
      };
    });
  }, [baseEdges, hoveredNodeId]);

  const handleNodeMouseEnter: NodeMouseHandler = useCallback(
    (_event, node) => setHoveredNodeId(node.id),
    [],
  );

  const handleNodeMouseLeave: NodeMouseHandler = useCallback(
    () => setHoveredNodeId(null),
    [],
  );

  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      const data = node.data as CompanyNodeData;
      if (data.nodeKind !== "company" || !data.siren) return;
      void openCompany(data.siren);
    },
    [openCompany],
  );

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-border" style={{ height }}>
      {openingSiren && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
          <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={{ type: "straight" }}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onInit={(instance) => {
          // Center the view on the central node (positioned at origin)
          instance.setCenter(0, 0, { zoom: 1 });
        }}
        nodesDraggable
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
        minZoom={0.2}
        maxZoom={2}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
      </ReactFlow>
    </div>
  );
}

// ─── Legend ────────────────────────────────────────────────────────────

function LegendItem({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-3 h-3 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ─── Relationship Table ──────────────────────────────────────────────

/**
 * What makes a table row open a related company: a click, or Enter or Space
 * once focused, as a button would. A row being looked up shows it and is busy.
 */
function relatedCompanyRowProps(
  siren: string | null | undefined,
  { openCompany, openingSiren }: OpenCompanyBySiren,
): ComponentProps<typeof TableRow> {
  if (!siren) return {};
  const open = () => void openCompany(siren);
  return {
    role: "button",
    tabIndex: 0,
    "aria-busy": openingSiren === siren,
    onClick: open,
    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      open();
    },
  };
}

/** The spinner of the row whose company is being looked up. */
function OpeningIndicator({
  siren,
  openingSiren,
}: {
  readonly siren: string | null | undefined;
  readonly openingSiren: string | null;
}) {
  if (!siren || openingSiren !== siren) return null;
  return (
    <Loader2Icon className="inline w-3 h-3 ml-1 animate-spin text-muted-foreground" />
  );
}

function PersonRow({
  person,
  opening,
}: {
  readonly person: PersonEntry;
  readonly opening: OpenCompanyBySiren;
}) {
  const [open, setOpen] = useState(false);
  const hasMandates = person.mandateCompanies.length > 0;

  return (
    <>
      <TableRow
        className={hasMandates ? "cursor-pointer hover:bg-muted/50" : undefined}
        onClick={hasMandates ? () => setOpen((v) => !v) : undefined}
      >
        <TableCell className="text-xs font-medium">
          <div className="flex items-center gap-1.5">
            {hasMandates && (
              <ChevronDownIcon
                className="w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform"
                style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}
              />
            )}
            <div>{person.firstName} {person.lastName}</div>
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {person.role ?? "—"}
        </TableCell>
      </TableRow>
      {open && person.mandateCompanies.map((mc, i) => (
        <TableRow
          key={`${person.key}-mc-${i}`}
          className={mc.siren ? "bg-muted/30 cursor-pointer hover:bg-muted/50" : "bg-muted/30"}
          {...relatedCompanyRowProps(mc.siren, opening)}
        >
          <TableCell className="text-xs pl-8 text-muted-foreground">
            {mc.name}
            {mc.siren && <span className="text-[10px] ml-1 opacity-60">{mc.siren}</span>}
            <OpeningIndicator siren={mc.siren} openingSiren={opening.openingSiren} />
          </TableCell>
          <TableCell className="text-xs text-muted-foreground">
            {mc.role ?? "—"}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function RelationshipTable({
  company,
  persons,
  opening,
}: {
  readonly company: CompanyDTO;
  readonly persons: PersonEntry[];
  readonly opening: OpenCompanyBySiren;
}) {
  const { t } = useTranslation();
  const companyRows = useMemo(() => buildCompanyRows(company), [company]);

  if (persons.length === 0 && companyRows.length === 0) return null;

  return (
    <ScrollArea className="h-[500px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">{t("company.relationships.name")}</TableHead>
            <TableHead className="text-xs">{t("company.relationships.role")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {persons.map((person) => (
            <PersonRow key={person.key} person={person} opening={opening} />
          ))}
          {companyRows.map((row: CompanyRow, i: number) => (
            <TableRow
              key={`company-${row.name}-${i}`}
              className={row.siren ? "cursor-pointer hover:bg-muted/50" : undefined}
              {...relatedCompanyRowProps(row.siren, opening)}
            >
              <TableCell className="text-xs font-medium">
                <div>
                  {row.name}
                  <OpeningIndicator siren={row.siren} openingSiren={opening.openingSiren} />
                </div>
                {row.detail && (
                  <div className="text-muted-foreground text-[11px]">{row.detail}</div>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {row.role ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────

export default function CardCompanyRelationshipGraph({
  company,
}: CardCompanyRelationshipGraphProps) {
  const { t } = useTranslation();
  const rel = company.corporateRelationships;
  // One for the graph, its full-screen copy and the table: a click in one
  // waits for a lookup started in another.
  const opening = useOpenCompanyBySiren();

  const persons = useMemo(() => buildPersonRegistry(company), [company]);
  const holdingCompanies = rel?.holdingCompanies ?? [];

  const hasData =
    persons.length > 0 ||
    holdingCompanies.length > 0 ||
    !!company.business.parentCompany ||
    !!rel?.acquiredBySummary ||
    (rel?.acquisitions ?? []).length > 0 ||
    (rel?.affiliatedCompanies ?? []).length > 0 ||
    !!rel?.domiciliationCompany;

  if (!hasData) return null;

  // Build dynamic legend
  const legendItems: { color: string; label: string }[] = [];

  if (persons.length > 0) {
    legendItems.push({
      color: PERSON_COLOR,
      label: t("company.relationships.executives"),
    });
  }
  if (company.business.parentCompany || holdingCompanies.length > 0) {
    legendItems.push({
      color: RELATIONSHIP_COLORS.parent,
      label: t("company.relationships.parentCompany"),
    });
  }
  if ((rel?.acquisitions ?? []).length > 0) {
    legendItems.push({
      color: RELATIONSHIP_COLORS.acquisition,
      label: t("company.relationships.acquisitions"),
    });
  }
  const hasAffiliates =
    (rel?.affiliatedCompanies ?? []).length > 0 ||
    (rel?.executiveMandates ?? []).length > 0;
  if (hasAffiliates) {
    legendItems.push({
      color: RELATIONSHIP_COLORS.affiliated,
      label: t("company.relationships.affiliatedCompanies"),
    });
  }
  if (rel?.domiciliationCompany) {
    legendItems.push({
      color: RELATIONSHIP_COLORS.domiciliation,
      label: t("company.relationships.domiciliation"),
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
              <NetworkIcon className="w-5 h-5" />
            </div>
            {t("company.relationships.cartography")}
            {persons.length > 0 && (
              <Badge variant="secondary" className="text-xs font-normal">
                {persons.length} {t("company.relationships.executive", { count: persons.length })}
              </Badge>
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className="relative flex-[3] min-w-0">
            <div className="absolute top-2 right-2 z-10 flex items-center gap-3">
              <div className="hidden sm:flex flex-wrap items-center gap-3">
                {legendItems.map((item) => (
                  <LegendItem
                    key={item.label}
                    color={item.color}
                    label={item.label}
                  />
                ))}
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted/80 transition-colors"
                    title={t("company.relationships.fullscreen")}
                  >
                    <MaximizeIcon className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <NetworkIcon className="w-5 h-5" />
                      {t("company.relationships.cartography")} — {company.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex-1 min-h-0">
                    <ReactFlowProvider>
                      <SpiderGraph company={company} opening={opening} height="100%" />
                    </ReactFlowProvider>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    {legendItems.map((item) => (
                      <LegendItem
                        key={item.label}
                        color={item.color}
                        label={item.label}
                      />
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <ReactFlowProvider>
              <SpiderGraph company={company} opening={opening} />
            </ReactFlowProvider>
          </div>
          <div className="flex-[2] min-w-0 border-l border-border pl-4">
            <RelationshipTable company={company} persons={persons} opening={opening} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
